import { inject, Service } from '@angular/core'
import { StorageService } from './storage.service'
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'
import { Platform } from '@ionic/angular'
import { v4 as uuidv4 } from 'uuid'
import { combinedCheckerHelper } from '../helpers/storage-checker.helper'
import { isNoMonitorAndProd } from '../helpers/check-for-monitor-and-prod.helper'
import { captureException } from '../helpers/error-capture.helper'
import { Capacitor } from '@capacitor/core'
import { initializeApp } from 'firebase/app'
import { AnalyticsEvents } from '../models/analytics-events'
import { LanguageService } from './language.service'

const ANALYTICS_ID_STORAGE_KEY = 'app-id'
const ANALYTICS_INIT_TRANSIENT_TAG = 'analytics-init-transient'

const TRANSIENT_ANALYTICS_INIT_PATTERNS: RegExp[] = [
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /load failed/i,
  /failed to fetch/i,
  /networkerror when attempting to fetch resource/i,
]

function isTransientAnalyticsInitError(error: unknown): boolean {
  let message: string
  if (error instanceof Error) {
    message = error.message
  } else if (typeof error === 'object' && error !== null && 'message' in error) {
    message = String((error as { message: unknown }).message)
  } else if (typeof error === 'string') {
    message = error
  } else {
    try {
      message = String(error)
    } catch {
      return false
    }
  }
  return TRANSIENT_ANALYTICS_INIT_PATTERNS.some((pattern) => pattern.test(message))
}

@Service()
export class FirebaseAnalyticsService {
  private storageService = inject(StorageService)
  private platform = inject(Platform)
  private languageService = inject(LanguageService)

  private id: string | null = null
  private startAnalytics = false
  private currentPage: { path: string; title: string } | null = null

  async init() {
    try {
      const shouldAnalyticsStart = isNoMonitorAndProd()
      await this.platform.ready()
      // if not a mobile app
      if (!Capacitor.isNativePlatform()) {
        if (!combinedCheckerHelper()) {
          this.startAnalytics = false
          await FirebaseAnalytics.setEnabled({
            enabled: false,
          })
          return
        }
        initializeApp({
          apiKey: 'AIzaSyB0Mpzbkqb0c1Wy622lw3A_E49j0avFF8k',
          authDomain: 'pregnant-with-diabetes.firebaseapp.com',
          projectId: 'pregnant-with-diabetes',
          storageBucket: 'pregnant-with-diabetes.appspot.com',
          messagingSenderId: '392075839540',
          appId: '1:392075839540:web:c220f23d21fb625a49a261',
          measurementId: 'G-BLNEVPF0B0',
        })
      }
      await FirebaseAnalytics.setEnabled({
        enabled: shouldAnalyticsStart,
      })

      if (!shouldAnalyticsStart) {
        return
      }

      this.id = await this.loadAnalyticsId()
      if (!this.id) {
        this.id = uuidv4()
      }
      await this.saveAnalyticsId(this.id)
      await FirebaseAnalytics.setUserId({
        userId: this.id,
      })
      this.startAnalytics = shouldAnalyticsStart
    } catch (error) {
      if (isTransientAnalyticsInitError(error)) {
        captureException(error, 'info', ANALYTICS_INIT_TRANSIENT_TAG)
      } else {
        captureException(error, 'error', 'analytics-init')
      }
    }
  }

  /**
   * StorageService silently no-ops while storage is unavailable, which used to
   * make the analytics id regenerate on every launch. Fall back to localStorage
   * so the id stays stable across launches during storage outages.
   */
  private async loadAnalyticsId(): Promise<string | null> {
    const stored = await this.storageService.get(ANALYTICS_ID_STORAGE_KEY)
    if (typeof stored === 'string' && stored) {
      return stored
    }
    try {
      return window.localStorage.getItem(ANALYTICS_ID_STORAGE_KEY) ?? null
    } catch (error) {
      captureException(error, 'info', 'analytics-id-local-storage-read')
      return null
    }
  }

  private async saveAnalyticsId(id: string): Promise<void> {
    await this.storageService.set(ANALYTICS_ID_STORAGE_KEY, id)
    try {
      window.localStorage.setItem(ANALYTICS_ID_STORAGE_KEY, id)
    } catch (error) {
      captureException(error, 'info', 'analytics-id-local-storage-write')
    }
  }

  platformName(): string {
    return Capacitor.getPlatform()
  }

  // The router path, not an origin-prefixed url, so screen names match on web, iOS and Android.
  async pageInit(path: string, title: string): Promise<void> {
    if (!this.startAnalytics) {
      return
    }
    this.currentPage = { path, title }
    try {
      const dashIndex = title?.indexOf('-')
      const screenClassOverride = dashIndex !== -1 ? title.substring(0, dashIndex) : title
      await FirebaseAnalytics.setCurrentScreen({
        screenName: path,
        screenClassOverride,
      })
    } catch (error) {
      captureException(error, 'error', 'analytics-page-init')
    }
    await this.trackEvent(AnalyticsEvents.PAGE_VIEW, {
      platform: this.platformName(),
    })
    // The native SDKs only ever send screen_view, so GA4's page-view surface
    // reads "not set" for iOS and Android. Web sends page_view by itself.
    if (Capacitor.isNativePlatform()) {
      await this.logEvent('page_view', {
        page_path: path,
        page_title: title,
        page_location: `${this.languageService.siteOrigin()}${path}`,
      })
    }
  }

  async trackEvent(name: string, params: object): Promise<void> {
    if (!this.startAnalytics) {
      return
    }
    await this.logEvent(`preg_${name.replaceAll('-', '_')}`, params)
  }

  private async logEvent(eventName: string, params: object): Promise<void> {
    try {
      await FirebaseAnalytics.logEvent({
        name: eventName,
        params: this.withCurrentPage(params),
      })
    } catch (error) {
      captureException(error, 'error', 'analytics-track-event')
    }
  }

  private withCurrentPage(params: object): object {
    if (!this.currentPage) {
      return { ...params }
    }
    return {
      page_path: this.currentPage.path,
      page_title: this.currentPage.title,
      ...params,
    }
  }

  async setUserProperty(name: string, value: string): Promise<void> {
    if (!this.startAnalytics) {
      return
    }
    try {
      await FirebaseAnalytics.setUserProperty({
        key: name,
        value,
      })
    } catch (error) {
      captureException(error, 'error', 'analytics-set-user-property')
    }
  }
}
